// ---------------------------------------------------------------------------
// MKT-011 Phase 2 — Marketplace recommendation scoring CQRS event catalog
// ---------------------------------------------------------------------------
// Story MKT-011: "Marketplace recommendations rank templates/apps based on
// business profile, vertical and verified ROI."
//
// Phase 1 landed the deterministic ranker in
// `packages/platform/src/services/marketplace/template-recommender.ts`:
//   score = verticalMatch * 0.45
//         + roiProxy      * 0.35
//         + ratingNorm    * 0.15
//         + installsNorm  * 0.05
//
// wired into `workers/api-platform/src/routes/biab/recommended-for-you.ts`
// (`/v1/marketplace/recommended-for-you`).
//
// This file is the Phase 2 CQRS event-catalog vocabulary tier — the single
// source of truth for the 2 CQRS event types the future event-router /
// audit-runner / future-A/B-against-learned-scorer evaluator MUST emit:
//   - `mkt.recommendation_scored@v1`  — one ranker decision was computed
//     for one tenant/listing pair, carrying the four score components so
//     downstream consumers can audit "why was this listing ranked here?"
//     without re-running the ranker against a snapshot of listing state.
//   - `mkt.recommendation_served@v1`  — the ranked list was returned from
//     the `/recommended-for-you` route for one tenant/request, carrying the
//     ordered listing-id list + the rendering surface so future click-through
//     joins can attribute installs back to the served recommendation slate.
//
// Per CLAUDE.md Governance Rule #6 ("Atomic Events"): one business fact per
// event. `scored@v1` records the per-listing ranker decision. `served@v1`
// records the per-request slate. Splitting them lets the future learned-
// scorer A/B harness join scored→served→installed without conflating the
// per-listing scoring fact with the per-request rendering fact.
//
// Per Governance Rule #7 ("Event Catalog"): every event type catalogued
// before implementation. The ranker exists today but does not emit any
// CQRS events; this catalog locks vocabulary first so the future emitter
// imports constants instead of hand-rolling drift-prone strings.
//
// What this file is NOT:
//   - It does not emit events — that ships with the route emitter wire-up.
//   - It does not declare Drizzle tables — vocabulary tier only.
//   - It does not change the Phase 1 ranker scoring formula.
// ---------------------------------------------------------------------------

/**
 * Closed catalog of the 2 MKT-011 marketplace recommendation CQRS event types.
 * Versioned with `@v1` suffix per platform convention so future shape changes
 * ship a `@v2` alongside (never mutate in place).
 *
 * - `mkt.recommendation_scored@v1`: ranker computed a score for one
 *   (tenantId, listingId) pair. Emitted once per scored listing per request.
 * - `mkt.recommendation_served@v1`: the ranked slate was returned for one
 *   (tenantId, requestId) pair. Emitted once per route response.
 */
export const MKT_011_RECOMMENDATION_EVENT_TYPES = [
  "mkt.recommendation_scored@v1",
  "mkt.recommendation_served@v1",
] as const;

export type Mkt011RecommendationEventType =
  (typeof MKT_011_RECOMMENDATION_EVENT_TYPES)[number];

/**
 * Runtime narrowing guard. DLQ consumers and the audit-runner use this to
 * reject unknown event types at the boundary instead of silently fanning
 * them downstream.
 */
export function isMkt011RecommendationEventType(
  value: unknown,
): value is Mkt011RecommendationEventType {
  return (
    typeof value === "string" &&
    (MKT_011_RECOMMENDATION_EVENT_TYPES as readonly string[]).includes(value)
  );
}

/**
 * Canonical mapping from the Phase 1 ranker's numeric `verticalMatch`
 * component to the categorical outcome string consumers carry on the
 * event payload. The numeric keys are the EXACT values produced by
 * `packages/platform/src/services/marketplace/template-recommender.ts`
 * (1.0 exact / 0.5 related / 0 none) — `@ease/db` cannot import the
 * ranker (dependency direction invariant), so this `Readonly<Record>`
 * is the single source of truth for both:
 *   - the catalog `MKT_011_VERTICAL_MATCH_OUTCOME_VOCAB` tuple
 *     (derived as `Object.values(...)`), and
 *   - the proof-file drift guard (which asserts the numeric keys
 *     match the ranker's documented outputs).
 *
 * Adding a fourth outcome MUST add a new numeric key here — never edit
 * the vocab tuple directly. The vocab is derived; the map is canonical.
 */
export const MKT_011_VERTICAL_MATCH_OUTCOME_BY_RANKER_SCORE = {
  1: "exact",
  0.5: "related",
  0: "none",
} as const;

/**
 * Closed catalog of vertical-match outcomes. Derived from the canonical
 * `MKT_011_VERTICAL_MATCH_OUTCOME_BY_RANKER_SCORE` map so the vocab can
 * never drift from the ranker → outcome bridge. The proof file pins the
 * derived tuple verbatim so the order + content fail loudly on edits.
 *
 * Tuple-typed via `satisfies` so TypeScript validates that every value
 * in the canonical map appears in the vocab in declaration order,
 * without losing the literal tuple type that downstream narrowing
 * depends on.
 */
export const MKT_011_VERTICAL_MATCH_OUTCOME_VOCAB = [
  MKT_011_VERTICAL_MATCH_OUTCOME_BY_RANKER_SCORE[1],
  MKT_011_VERTICAL_MATCH_OUTCOME_BY_RANKER_SCORE[0.5],
  MKT_011_VERTICAL_MATCH_OUTCOME_BY_RANKER_SCORE[0],
] as const satisfies readonly [
  (typeof MKT_011_VERTICAL_MATCH_OUTCOME_BY_RANKER_SCORE)[1],
  (typeof MKT_011_VERTICAL_MATCH_OUTCOME_BY_RANKER_SCORE)[0.5],
  (typeof MKT_011_VERTICAL_MATCH_OUTCOME_BY_RANKER_SCORE)[0],
];

export type Mkt011VerticalMatchOutcome =
  (typeof MKT_011_VERTICAL_MATCH_OUTCOME_VOCAB)[number];

export function isMkt011VerticalMatchOutcome(
  value: unknown,
): value is Mkt011VerticalMatchOutcome {
  return (
    typeof value === "string" &&
    (MKT_011_VERTICAL_MATCH_OUTCOME_VOCAB as readonly string[]).includes(value)
  );
}

/**
 * Closed catalog of the surfaces that can render the ranked slate. Pinned
 * so a future surface addition (e.g., agency-portal marketplace) cannot
 * smuggle in an unknown surface string before the catalog is bumped.
 *
 * - `web_recommended_for_you`: the mounted `/v1/marketplace/recommended-for-you`
 *   route surface (the Phase 1 wire-up at `workers/api-platform/src/routes/biab/recommended-for-you.ts`).
 * - `web_marketplace_browse`: future surface where ranker boosts default sort.
 * - `onboarding_first_run`: future surface where ranker drives first-run picks.
 */
export const MKT_011_RECOMMENDATION_SURFACE_VOCAB = [
  "web_recommended_for_you",
  "web_marketplace_browse",
  "onboarding_first_run",
] as const;

export type Mkt011RecommendationSurface =
  (typeof MKT_011_RECOMMENDATION_SURFACE_VOCAB)[number];

export function isMkt011RecommendationSurface(
  value: unknown,
): value is Mkt011RecommendationSurface {
  return (
    typeof value === "string" &&
    (MKT_011_RECOMMENDATION_SURFACE_VOCAB as readonly string[]).includes(value)
  );
}

/**
 * Closed catalog of ranker variants. `deterministic_v1` pins the Phase 1
 * hand-built ranker so a future learned-scorer A/B harness can attribute
 * installs back to the scorer that ranked them. Adding a learned variant
 * MUST extend this tuple + bump the catalog.
 */
export const MKT_011_RANKER_VARIANT_VOCAB = ["deterministic_v1"] as const;

export type Mkt011RankerVariant =
  (typeof MKT_011_RANKER_VARIANT_VOCAB)[number];

export function isMkt011RankerVariant(
  value: unknown,
): value is Mkt011RankerVariant {
  return (
    typeof value === "string" &&
    (MKT_011_RANKER_VARIANT_VOCAB as readonly string[]).includes(value)
  );
}

// ---------------------------------------------------------------------------
// Per-event payload contracts
// ---------------------------------------------------------------------------
// Each payload is the minimal set of fields the consumer needs. Emitters MUST
// include `tenantId` on every event so cross-event correlation (audit, replay)
// does not require a join. The `requestId` is supplied by the route emitter
// so all `scored@v1` events for one request can be joined to the single
// paired `served@v1` event without joining on payload shapes.

/**
 * Per-component score breakdown. Mirrors the Phase 1 ranker's
 * `RecommendationScore.components` shape verbatim so the audit-runner can
 * verify ranking decisions without re-running the ranker. Drift between the
 * library tier and this shape would silently break per-component rollups.
 */
export interface Mkt011ScoreComponents {
  /** Vertical-match component in [0, 1]. 1.0 exact, 0.5 related, 0 none. */
  verticalMatch: number;
  /** ROI proxy component in [0, 1]. Derived from avgConversionRatePercent / 10. */
  roiProxy: number;
  /** Rating component in [0, 1]. avgRating / 5. */
  ratingNorm: number;
  /** Installs component in [0, 1]. log10(installs+1) / 4 clamped. */
  installsNorm: number;
}

export interface Mkt011RecommendationScoredEventPayload {
  /** Tenant scope for fan-out + isolation. */
  tenantId: string;
  /**
   * Stable identifier of the marketplace listing that was scored. Used to
   * join scored→served→installed without joining on payload shapes.
   */
  listingId: string;
  /**
   * Stable identifier of the route request this scoring decision was part
   * of. Pairs every `scored@v1` for one request with the single `served@v1`
   * event that closed it.
   */
  requestId: string;
  /** Closed ranker-variant vocabulary; pins which scorer produced the score. */
  rankerVariant: Mkt011RankerVariant;
  /** Composite score; weighted sum of components per Phase 1 formula. */
  score: number;
  /** Per-component score breakdown mirroring the Phase 1 ranker shape. */
  components: Mkt011ScoreComponents;
  /** Closed vertical-match outcome (audit-friendly categorical form). */
  verticalMatchOutcome: Mkt011VerticalMatchOutcome;
  /** ISO-8601 when the scoring decision was recorded. */
  scoredAt: string;
}

export interface Mkt011RecommendationServedEventPayload {
  /** Tenant scope for fan-out + isolation. */
  tenantId: string;
  /**
   * Same `requestId` as the paired `scored@v1` events so the audit-runner
   * can verify the slate was rendered from the same scoring batch.
   */
  requestId: string;
  /** Closed surface vocabulary; pins where the slate was rendered. */
  surface: Mkt011RecommendationSurface;
  /**
   * Ordered list of listing ids in served order. Order is significant —
   * downstream click-through joins attribute installs to slot position.
   */
  servedListingIds: readonly string[];
  /**
   * Total number of listings considered before slate truncation. Lets the
   * audit-runner verify the served slate is a prefix of the scored set.
   */
  consideredCount: number;
  /** ISO-8601 when the slate was returned from the route. */
  servedAt: string;
}

/**
 * Discriminated union mapping each event type to its payload. Consumers
 * (route emitter, DLQ, audit-runner, future learned-scorer A/B harness)
 * can `switch (event.type)` and pull the right payload shape without
 * any-casting.
 */
export type Mkt011RecommendationEvent =
  | {
      type: "mkt.recommendation_scored@v1";
      payload: Mkt011RecommendationScoredEventPayload;
    }
  | {
      type: "mkt.recommendation_served@v1";
      payload: Mkt011RecommendationServedEventPayload;
    };
